import { i18n } from 'boot/i18n';

export const TARGET_STATUSES = {
  ABORTED: 'aborted',
  ACQUISITION: 'acquisition',
  CLOSED: 'closed',
  HOLD: 'hold',
  MASTERED: 'mastered',
  PROBE: 'probe',
  WAITING: 'waiting',
};

export const TARGET_DATA_TYPES = /** @type {const} */ ({
  CAB_BRANCH: 'cabBranch',
  DISCRETE_TRIAL: 'discreteTrial',
  DURATION: 'duration',
  FREQUENCY: 'frequency',
  INSTRUCTIONS: 'instructions',
  INTERVAL: 'interval',
  RANDOM_ROTATION: 'randomRotation',
  RATE: 'rate',
  SHAPING: 'shaping',
  TARGET_SET: 'targetSet',
  TASK_ANALYSIS: 'taskAnalysis',
});

export const COMPATIBLE_TARGET_DATA_TYPES_BY_METRIC_GROUP =/** @type {const} */ ({
  duration: [TARGET_DATA_TYPES.DURATION, TARGET_DATA_TYPES.INSTRUCTIONS],
  percentCorrect: [
    TARGET_DATA_TYPES.DISCRETE_TRIAL,
    TARGET_DATA_TYPES.TASK_ANALYSIS,
    TARGET_DATA_TYPES.TARGET_SET,
    TARGET_DATA_TYPES.SHAPING,
    TARGET_DATA_TYPES.INTERVAL,
    TARGET_DATA_TYPES.INSTRUCTIONS,
    // RANDOM and CAB are stripped from the list, unless it is a SBT program.
    TARGET_DATA_TYPES.RANDOM_ROTATION,
    TARGET_DATA_TYPES.CAB_BRANCH,
  ],
  frequency: [TARGET_DATA_TYPES.FREQUENCY, TARGET_DATA_TYPES.INSTRUCTIONS],
  rate: [TARGET_DATA_TYPES.RATE, TARGET_DATA_TYPES.INSTRUCTIONS],
});

export const TARGET_DATA_TYPE_DEFINITIONS = /** @type {const} */ ([
  {
    value: TARGET_DATA_TYPES.DISCRETE_TRIAL,
    label: i18n.t('programs.targets.dataTypes.discreteTrial'),
    showType: false,
    hasSettings: false,
    hasSubTargets: false,
    allowAsSubTarget: true,
    hasBaseline: true,
    showMultipleSubTargets: false,
    hasDiscriminativeStimulus: true,
    hasMaintenanceInterval: true,
    hasWorkflow: true,
    hasPromptLevelTemplate: true,
    hasInstructions: false,
    excludeFromProgressCount: false,
    excludeSubTargetsFromProgressCount: false,
    allowedProgramTypes: ['simple', 'behaviorTracking', 'sbt', 'ptp', 'asr'],
    allowedMetrics: ['percentCorrect', 'frequency'],
    passThroughMetrics: ['percentCorrect', 'frequency'],
    defaultMetric: 'percentCorrect',
  },
  {
    value: TARGET_DATA_TYPES.DURATION,
    label: i18n.t('programs.targets.dataTypes.duration'),
    showType: true,
    hasSettings: true,
    hasSubTargets: false,
    allowAsSubTarget: true,
    hasBaseline: true,
    showMultipleSubTargets: false,
    hasDiscriminativeStimulus: true,
    hasMaintenanceInterval: true,
    hasWorkflow: true,
    hasPromptLevelTemplate: true,
    hasInstructions: false,
    excludeFromProgressCount: false,
    excludeSubTargetsFromProgressCount: false,
    allowedProgramTypes: ['simple', 'behaviorTracking', 'sbt', 'ptp', 'asr'],
    allowedMetrics: ['percentCorrect', 'frequency', 'totalDuration', 'minDuration', 'maxDuration', 'avgDuration'],
    passThroughMetrics: ['totalDuration', 'minDuration', 'maxDuration', 'avgDuration'],
    defaultMetric: 'totalDuration',
  },
  {
    value: TARGET_DATA_TYPES.RATE,
    label: i18n.t('programs.targets.dataTypes.rate'),
    showType: true,
    hasSettings: true,
    hasSubTargets: false,
    hasBaseline: true,
    showMultipleSubTargets: false,
    hasDiscriminativeStimulus: true,
    hasMaintenanceInterval: true,
    hasWorkflow: true,
    hasPromptLevelTemplate: true,
    hasInstructions: false,
    excludeFromProgressCount: false,
    excludeSubTargetsFromProgressCount: false,
    allowedProgramTypes: ['simple', 'behaviorTracking', 'sbt', 'ptp', 'asr'],
    allowedMetrics: ['percentCorrect', 'frequency', 'ratePerHour', 'ratePerMinute'],
    passThroughMetrics: ['ratePerHour', 'ratePerMinute'],
    defaultMetric: 'ratePerHour',
  },
  {
    value: TARGET_DATA_TYPES.TASK_ANALYSIS,
    label: i18n.t('programs.targets.dataTypes.taskAnalysis'),
    showType: true,
    hasSettings: true,
    hasSubTargets: true,
    hasBaseline: true,
    allowAsSubTarget: false,
    showMultipleSubTargets: true,
    settingsLabel: i18n.t('programs.targetCard.buttonEditSteps'),
    readOnlySettingsLabel: i18n.t('programs.targetCard.buttonViewSteps'),
    hasDiscriminativeStimulus: true,
    hasMaintenanceInterval: true,
    hasWorkflow: false,
    hasSubTargetMaintenanceInterval: false,

    hasDefaultObservation: true,
    hasPromptLevelTemplate: true,
    hasInstructions: false,
    excludeFromProgressCount: false,
    excludeSubTargetsFromProgressCount: true,
    allowedProgramTypes: ['simple', 'behaviorTracking', 'sbt', 'asr'],
  },
  {
    value: TARGET_DATA_TYPES.TARGET_SET,
    label: i18n.t('programs.targets.dataTypes.targetSet'),
    showType: true,
    hasSettings: true,
    hasSubTargets: true,
    hasBaseline: false,
    allowAsSubTarget: false,
    showMultipleSubTargets: true,
    settingsLabel: i18n.t('programs.targetCard.buttonEditSubTargets'),
    readOnlySettingsLabel: i18n.t('programs.targetCard.buttonViewSubTargets'),
    hasDiscriminativeStimulus: false,
    hasMaintenanceInterval: false,
    hasWorkflow: false,
    hasSubTargetMastering: true,
    hasSubTargetMaintenanceInterval: true,
    preventWorkflowInheritance: true,
    hasPromptLevelTemplate: true,
    hasInstructions: false,
    excludeFromProgressCount: true,
    excludeSubTargetsFromProgressCount: false,
    allowedProgramTypes: ['simple', 'behaviorTracking', 'sbt', 'ptp', 'asr'],
  },
  {
    value: TARGET_DATA_TYPES.SHAPING,
    label: i18n.t('programs.targets.dataTypes.shaping'),
    showType: true,
    hasSettings: true,
    hasSubTargets: true,
    hasBaseline: false,
    allowAsSubTarget: false,
    showMultipleSubTargets: false,
    settingsLabel: i18n.t('programs.targetCard.buttonEditSubTargets'),
    readOnlySettingsLabel: i18n.t('programs.targetCard.buttonViewSubTargets'),
    hasDiscriminativeStimulus: false,
    hasMaintenanceInterval: false,
    hasWorkflow: true,
    hasSubTargetMaintenanceInterval: false,
    hasPromptLevelTemplate: true,
    hasInstructions: false,
    excludeFromProgressCount: true,
    excludeSubTargetsFromProgressCount: false,
    allowedProgramTypes: ['simple', 'behaviorTracking', 'sbt', 'asr'],
  },
  {
    value: TARGET_DATA_TYPES.RANDOM_ROTATION,
    label: i18n.t('programs.targets.dataTypes.randomRotation'),
    showType: true,
    hasSettings: true,
    hasSubTargets: true,
    hasBaseline: false,
    allowAsSubTarget: true,
    showMultipleSubTargets: false,
    settingsLabel: i18n.t('programs.targetCard.buttonEditSubTargets'),
    readOnlySettingsLabel: i18n.t('programs.targetCard.buttonViewSubTargets'),
    hasDiscriminativeStimulus: false,
    hasMaintenanceInterval: false,
    hasWorkflow: true,
    hasSubTargetMaintenanceInterval: true,
    limitPrograms: '',
    hasPromptLevelTemplate: true,
    hasInstructions: false,
    excludeFromProgressCount: true,
    excludeSubTargetsFromProgressCount: false,
    allowedProgramTypes: ['sbt'],
  },
  {
    value: TARGET_DATA_TYPES.CAB_BRANCH,
    label: i18n.t('programs.targets.dataTypes.cabBranch'),
    showType: true,
    hasSettings: true,
    hasSubTargets: true,
    hasBaseline: false,
    allowAsSubTarget: false,
    showMultipleSubTargets: true,
    settingsLabel: i18n.t('programs.targetCard.buttonEditBranchTargets'),
    readOnlySettingsLabel: i18n.t('programs.targetCard.buttonViewBranchTargets'),
    hasDiscriminativeStimulus: false,
    hasMaintenanceInterval: false,
    hasWorkflow: false,
    hasSubTargetMaintenanceInterval: false,
    excludeFromProgressCount: true,
    excludeSubTargetsFromProgressCount: false,
    allowedProgramTypes: ['sbt'],
  },
  {
    value: TARGET_DATA_TYPES.INSTRUCTIONS,
    label: i18n.t('programs.targets.dataTypes.instructions'),
    showType: true,
    hasSettings: true,
    hasSubTargets: false,
    hasBaseline: false,
    allowAsSubTarget: false,
    showMultipleSubTargets: false,
    hasDiscriminativeStimulus: false,
    hasMaintenanceInterval: false,
    hasWorkflow: false,
    hasPromptLevelTemplate: false,
    hasInstructions: true,
    hasSubTargetMaintenanceInterval: false,
    excludeFromProgressCount: true,
    excludeSubTargetsFromProgressCount: false,
    allowedProgramTypes: ['simple', 'behaviorTracking', 'sbt', 'ptp', 'asr'],
  },
  {
    value: TARGET_DATA_TYPES.INTERVAL,
    label: i18n.t('programs.targets.dataTypes.interval'),
    showType: true,
    hasSettings: true,
    hasSubTargets: true,
    allowAsSubTarget: true,
    showMultipleSubTargets: true,
    settingsLabel: i18n.t('programs.targetCard.buttonEditSubTargets'),
    readOnlySettingsLabel: i18n.t('programs.targetCard.buttonViewSubTargets'),
    hasDiscriminativeStimulus: false,
    hasMaintenanceInterval: false,
    hasWorkflow: false,
    hasSubTargetMaintenanceInterval: true,
    hasSubTargetMastering: false,
    hasPromptLevelTemplate: true,
    hasDefaultObservation: true,
    hasIntervalSettings: true,
    hasInstructions: false,
    excludeFromProgressCount: true,
    excludeSubTargetsFromProgressCount: false,
    allowedProgramTypes: ['simple', 'behaviorTracking'],
    allowedMetrics: ['percentCorrect', 'frequency'],
    passThroughMetrics: ['percentCorrect', 'frequency'],
    defaultMetric: 'percentCorrect',
  },
  {
    value: TARGET_DATA_TYPES.FREQUENCY,
    label: i18n.t('programs.targets.dataTypes.frequency'),
    showType: true,
    hasSettings: true,
    hasSubTargets: false,
    allowAsSubTarget: false,
    showMultipleSubTargets: false,
    hasDiscriminativeStimulus: true,
    hasMaintenanceInterval: true,
    hasWorkflow: true,
    hasPromptLevelTemplate: true,
    hasInstructions: false,
    excludeFromProgressCount: false,
    excludeSubTargetsFromProgressCount: false,
    allowedProgramTypes: ['simple', 'behaviorTracking'],
    allowedMetrics: ['frequency'],
    passThroughMetrics: ['frequency'],
    defaultMetric: 'frequency',
  },
]);

export const TARGET_COLUMN_DEFS = [
  { value: 'label', labelKey: 'programs.targets.properties.label', sortable: true },
  { value: 'discriminativeStimulus', labelKey: 'programs.targets.properties.discriminativeStimulus', sortable: true },
  { value: 'status', labelKey: 'programs.targets.properties.status' },
  { value: 'introductionDate', labelKey: 'programs.targets.properties.introductionDate', sortable: true },
  { value: 'masteryDate', labelKey: 'programs.targets.properties.masteryDate', sortable: true },
  { value: 'trialsToAcquire', labelKey: 'programs.targets.properties.trialsToAcquire' },
  { value: 'sessionsToAcquire', labelKey: 'programs.targets.properties.sessionsToAcquire' },
];
